// Wires up the Event RSVP landing page's status-change buttons.
//
// Cancel (SA via SP 122, elevated via SP 119): the button sits inside an a11y-dialog modal
// already initialized site-wide by initModals(). Clicking POSTs to the matching endpoint and,
// on success, reloads the page so the cancelled reservation reappears as reservable.
//
// Re-reserve (elevated only): not modal-gated, since nothing is destroyed. SP 119 answers this
// direction with the reservation form's URL rather than flipping the status, so the POST returns
// a query string which we append to the reservation page and navigate to.

const SA_ENDPOINT = '/api/EventRsvp/CancelSa';
const ELEVATED_ENDPOINT = '/api/EventRsvp/CancelElevated';
const REACTIVATE_ENDPOINT = '/api/EventRsvp/ReactivateElevated';
const GENERIC_ERROR = "We couldn't cancel your reservation. Please try again.";

// Resolves the POST endpoint + body for a cancel button, or null if it carries no valid id.
function resolveCancelRequest(button: HTMLButtonElement): { url: string; body: object } | null {
    const saNo = parseInt(button.getAttribute('data-cancel-sa') || '', 10);
    if (saNo) return { url: SA_ENDPOINT, body: { sa_no: saNo } };

    const evexNo = parseInt(button.getAttribute('data-cancel-evex') || '', 10);
    if (evexNo) {
        // new_status comes from SP 115's button_url (19 for elevated, where SA uses 13). Sent
        // through rather than hardcoded server-side; 0 lets the server fall back to config.
        const newStatus = parseInt(button.getAttribute('data-cancel-new-status') || '', 10);
        return {
            url: ELEVATED_ENDPOINT,
            body: { evex_no: evexNo, new_status: isNaN(newStatus) ? 0 : newStatus },
        };
    }

    return null;
}

async function cancelReservation(button: HTMLButtonElement): Promise<void> {
    const request = resolveCancelRequest(button);
    if (!request) return;

    // Modal-gated buttons keep their error line inside the dialog; the un-gated re-activate
    // button has one as a sibling instead, so fall back to the immediate parent.
    const errorEl = (button.closest('.event-rsvp-cancel-modal__content') ?? button.parentElement)
        ?.querySelector('.event-rsvp-cancel-modal__error') as HTMLElement | null;

    button.disabled = true;
    if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = '';
    }

    try {
        const response = await fetch(request.url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(request.body),
        });

        if (response.ok) {
            const data = await response.json();
            if (data && data.success) {
                window.location.reload();
                return;
            }
        }
        throw new Error('Cancel failed');
    } catch {
        button.disabled = false;
        if (errorEl) {
            errorEl.textContent = GENERIC_ERROR;
            errorEl.hidden = false;
        }
    }
}

// Re-reserving a cancelled elevated invitation. SP 119 answers with the reservation form's URL
// instead of flipping the status, so we resolve that server-side and then navigate to the form —
// the patron still has to submit it for the reservation to exist.
async function reactivateReservation(button: HTMLButtonElement): Promise<void> {
    const evexNo = parseInt(button.getAttribute('data-reactivate-evex') || '', 10);
    if (!evexNo) return;

    const newStatus = parseInt(button.getAttribute('data-reactivate-new-status') || '', 10);
    const base = button.getAttribute('data-reactivate-base') || '';
    const errorEl = button.parentElement
        ?.querySelector('.event-rsvp-cancel-modal__error') as HTMLElement | null;

    button.disabled = true;
    if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = '';
    }

    try {
        const response = await fetch(REACTIVATE_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                evex_no: evexNo,
                new_status: isNaN(newStatus) ? 0 : newStatus,
            }),
        });

        if (response.ok) {
            const data = await response.json();
            if (data && data.success && data.query && base) {
                const returnUrl = encodeURIComponent(window.location.pathname);
                const sep = data.query.indexOf('?') === 0 ? '&' : '?';
                window.location.assign(`${base}${data.query}${sep}returnUrl=${returnUrl}`);
                return;
            }
        }
        throw new Error('Reactivate failed');
    } catch {
        button.disabled = false;
        if (errorEl) {
            errorEl.textContent = GENERIC_ERROR;
            errorEl.hidden = false;
        }
    }
}

export function initEventRsvpCancel(): void {
    const cancelButtons = Array.prototype.slice.call(
        document.querySelectorAll('[data-cancel-sa], [data-cancel-evex]')
    ) as HTMLButtonElement[];

    cancelButtons.forEach(button => {
        button.addEventListener('click', () => {
            void cancelReservation(button);
        });
    });

    const reactivateButtons = Array.prototype.slice.call(
        document.querySelectorAll('[data-reactivate-evex]')
    ) as HTMLButtonElement[];

    reactivateButtons.forEach(button => {
        button.addEventListener('click', () => {
            void reactivateReservation(button);
        });
    });
}
